import * as React from "react"
import { createPortal } from "react-dom"

export type ModalShellProps = {
  ariaDescribedby?: string
  ariaLabelledby?: string
  children: React.ReactNode
  className?: string
  closeButtonClassName?: string
  closeButtonLabel?: string
  initialFocusRef?: { current: HTMLElement | null }
  isOpen?: boolean
  onClose?: () => void
  overlayClassName?: string
  showCloseButton?: boolean
}

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ")
}

export const ModalShell: React.FC<ModalShellProps> = ({
  ariaDescribedby,
  ariaLabelledby,
  children,
  className,
  closeButtonClassName,
  closeButtonLabel = "Close modal",
  initialFocusRef,
  isOpen = false,
  onClose,
  overlayClassName,
  showCloseButton = true,
}) => {
  const onCloseRef = React.useRef(onClose)
  const initialFocusTargetRef = React.useRef(initialFocusRef)
  const overlayRef = React.useRef<HTMLDivElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const previousActiveElementRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  React.useEffect(() => {
    initialFocusTargetRef.current = initialFocusRef
  }, [initialFocusRef])

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined
    }

    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight
    const hiddenSiblingRecords: Array<{
      element: HTMLElement
      ariaHidden: string | null
      inert: string | null
    }> = []

    document.body.style.overflow = "hidden"
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    const frameId = window.requestAnimationFrame(() => {
      const overlayElement = overlayRef.current

      if (overlayElement) {
        Array.from(document.body.children).forEach(child => {
          if (!(child instanceof HTMLElement) || child === overlayElement) {
            return
          }

          hiddenSiblingRecords.push({
            element: child,
            ariaHidden: child.getAttribute("aria-hidden"),
            inert: child.getAttribute("inert"),
          })
          child.setAttribute("aria-hidden", "true")
          child.setAttribute("inert", "")
        })
      }

      const initialTarget = initialFocusTargetRef.current?.current
      const fallbackTarget =
        getFocusableElements(panelRef.current)[0] ?? panelRef.current

      ;(initialTarget ?? fallbackTarget)?.focus()
    })

    function getFocusableElements(container: HTMLElement | null) {
      if (!container) {
        return []
      }

      return Array.from(
        container.querySelectorAll<HTMLElement>(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])",
          ].join(","),
        ),
      ).filter(element => {
        const style = window.getComputedStyle(element)
        const rect = element.getBoundingClientRect()

        return (
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          rect.width > 0 &&
          rect.height > 0 &&
          element.getAttribute("aria-hidden") !== "true"
        )
      })
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault()
        onCloseRef.current?.()
        return
      }

      if (event.key !== "Tab") {
        return
      }

      const focusableElements = getFocusableElements(panelRef.current)

      if (focusableElements.length === 0) {
        event.preventDefault()
        panelRef.current?.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
        return
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      window.cancelAnimationFrame(frameId)
      document.body.style.overflow = originalOverflow
      document.body.style.paddingRight = originalPaddingRight
      hiddenSiblingRecords.forEach(record => {
        if (record.ariaHidden === null) {
          record.element.removeAttribute("aria-hidden")
        } else {
          record.element.setAttribute("aria-hidden", record.ariaHidden)
        }

        if (record.inert === null) {
          record.element.removeAttribute("inert")
        } else {
          record.element.setAttribute("inert", record.inert)
        }
      })

      const previousActiveElement = previousActiveElementRef.current

      if (previousActiveElement?.isConnected) {
        previousActiveElement.focus()
      }
    }
  }, [isOpen])

  if (!isOpen || typeof document === "undefined") {
    return null
  }

  const overlayClasses = [
    "fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-scrim-900 px-4 pt-12 pb-6 lg:items-center lg:pt-6",
    overlayClassName,
  ]
    .filter(Boolean)
    .join(" ")

  const panelClasses = [
    "relative w-full max-w-[calc(100vw-32px)] outline-none",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  return createPortal(
    <div
      className={overlayClasses}
      onClick={event => {
        if (event.target === event.currentTarget) {
          onCloseRef.current?.()
        }
      }}
      ref={overlayRef}
    >
      <div
        aria-describedby={ariaDescribedby}
        aria-labelledby={ariaLabelledby}
        aria-modal="true"
        className={panelClasses}
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        {showCloseButton ? (
          <button
            aria-label={closeButtonLabel}
            className={joinClasses(
              "absolute right-[11px] top-[11px] z-10 flex size-[25px] cursor-pointer items-center justify-center rounded-full border-2 border-ink-950 bg-accent-500 text-ink-950 outline-offset-2 lg:right-[12px] lg:top-[12px] lg:size-[35px]",
              closeButtonClassName,
            )}
            onClick={() => onCloseRef.current?.()}
            type="button"
          >
            <span aria-hidden="true" className="relative block size-full">
              <span className="absolute left-1/2 top-1/2 h-[2px] w-[12px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-ink-950 lg:w-[16px]" />
              <span className="absolute left-1/2 top-1/2 h-[2px] w-[12px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-ink-950 lg:w-[16px]" />
            </span>
          </button>
        ) : null}

        {children}
      </div>
    </div>,
    document.body,
  )
}
